import React from 'react';
import PropTypes from 'prop-types';
import {inject, observer} from 'mobx-react';
import {action, computed, observable, reaction} from 'mobx';
import {Link, routerShape, withRouter} from 'react-router';

import {withConfig, withI18n} from 'spa/Components/Context';
import autobind from 'autobind-decorator';

import Cookies from 'js-cookie';

import Button, {ButtonType} from '@urbanmassage/component-library/lib/Button/Button';

import AccountDetails from './Header/AccountDetails';
import Basket from 'spa/Pages/Booking/Basket';
import ContextualMenu from 'spa/Components/ContextualMenu';
import CountrySelect from './Header/CountrySelect';
import DisableBodyScroll from './DisableBodyScroll';
import HeaderBasketSectionWrapper from './HeaderBasketSectionWrapper/HeaderBasketSectionWrapper';
import Logo from './Header/Logo';
import PageMenu from './Header/PageMenu';
import ProgressiveImage from './ProgressiveImage';
import RenderToContextual from 'spa/Components/RenderToContextual';
import SessionSplit from './SessionSplit';
import Tooltip from 'spa/Components/Tooltip';

import hamburger from 'spa/images/icons/hamburger.svg';

const REFERRAL_HOME_POPUP_COOKIE_NAME = 'referral_home_popup_dismissed';

@withRouter
@inject(({
  store: {
    address,
    basket,
    notifications,
    selection,
    session,
    tracker,
    navigation,
  },
}) => (
  {
    address,
    basket,
    notifications,
    selection,
    session,
    tracker,
    navigation,
  }
))
@observer
@withConfig
@withI18n
class Header extends React.Component {
  static propTypes = {
    address: PropTypes.shape({
      error: PropTypes.object,
      isLoading: PropTypes.bool.isRequired,
      load: PropTypes.func.isRequired,
      response: PropTypes.shape({
        servicedArea: PropTypes.string,
      }),
    }).isRequired,
    basket: PropTypes.object.isRequired,
    notifications: PropTypes.object.isRequired,
    router: routerShape.isRequired,
    selection: PropTypes.object.isRequired,
    session: PropTypes.shape({
      user: PropTypes.object,
    }).isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
    useHeading: PropTypes.bool,
    navigation: PropTypes.shape({
      getNavigation: PropTypes.func.isRequired,
      header: PropTypes.object,
    }).isRequired,
  };

  state = { menuIsOpen: false };

  UNSAFE_componentWillMount() {
    const {config, i18n} = this.context;
    const {navigation} = this.props;

    navigation.getNavigation(i18n.lc);
    this.friendReferralDiscount = config.getValue('b2c.referral-friend-gets-value', i18n.umRegion);
    this.giftsEnabled = config.getValue('feature.gift-vouchers-enabled', i18n.umRegion);
  }

  componentDidMount() {
    const { i18n } = this.context;
    this.componentWillUnmount = this.props.router.listen(this.routerWillLeave);

    this.listeners = [
      reaction(
        () => this.props.session && this.props.session.user,
        (user) => {
          if (user) {
            this.userLoggedIn = true;

            if (user.meta && user.meta.stats) {
              const {totalBookings, upcomingBookings} = user.meta.stats;

              this.hasBooked = totalBookings + upcomingBookings;
            }

            if (this.userLoggedIn && this.hasBooked && !Cookies.get(REFERRAL_HOME_POPUP_COOKIE_NAME)) {
              this.showReferralPopup = true;
            }
          } else {
            this.userLoggedIn = false;
            this.hasBooked = false;
            this.showReferralPopup = false;
          }
        },
        {
          name: 'Check if user is logged in',
          compareStructural: true,
          fireImmediately: true,
        },
      ),
      reaction(
        () => [
          this.props.basket && this.props.basket.basketItems,
          this.props.basket && this.props.basket.basketServiceArea,
        ],
        ([basketItems, basketServiceArea]) => {
          if (basketItems && basketServiceArea) {
            this.props.basket.validateBasket.put(`serviceArea=${basketServiceArea}`, this.props.basket.basketInfo)
              .then((response) => {
                const {basket, selection} = this.props;

                basket.validatedBasket = response;

                selection.basketItems = this.props.basket.basketItems;
                selection.basketValidated = true;
                selection.basketWorkerCount = this.props.basket.workerCount;
              }, (error) => {
                const {notifications, selection} = this.props;

                selection.basketValidated = false;

                this.props.basket.basketItems = [];

                this.props.selection.basketConfirmed = false;

                this.props.selection.selectedCategory = null;

                this.props.selection.selectedVertical = null;

                this.props.selection.verticalConfirmed = false;

                notifications.add({
                  id: 'config-error',
                  type: 'error',
                  title: i18n._x('Your basket is not valid and has been cleared.', 'Booking flow'),
                });
              });
          }
        },
        {
          compareStructural: true,
          delay: 500,
          fireImmediately: true,
          name: 'Validate basket on startup in header',
        },
      ),
    ];
  }

  componentWillUnmount() {
    this.setState({menuIsOpen: false});

    while (this.listeners && this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable showBasketPanel = false;
  @observable showReferralPopup = false;
  @observable userLoggedIn = false;

  @autobind onChangeMenuCheckbox(event) {
    this.setState({
      menuIsOpen: event.target.checked,
    });
  }

  @autobind routerWillLeave() {
    this.setState({menuIsOpen: false});

    while (this.listeners && this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @autobind
  @action('clickedReferNow - Header')
  clickedReferNow(event) {
    if (event) event.preventDefault();

    const {router, tracker} = this.props;
    const {i18n} = this.context;

    this.showReferralPopup = false;

    Cookies.set(REFERRAL_HOME_POPUP_COOKIE_NAME, 'true');

    tracker.track('Pressed Refer Now on header refer tooltip', {
      category: 'Referral header tooltip',
    });

    router.push(`/${i18n.lc}/account/refer`);
  }

  @autobind
  @action('onCloseBasketPanel - Header')
  onCloseBasketPanel(event) {
    const {tracker} = this.props;

    this.showBasketPanel = false;

    tracker.track('Clicked to close basket panel', {
      category: 'Booking',
    });
  }

  @autobind
  @action('onCloseTooltip - Header')
  onCloseTooltip(event) {
    if (event) event.preventDefault();

    const {tracker} = this.props;

    this.showReferralPopup = false;

    Cookies.set(REFERRAL_HOME_POPUP_COOKIE_NAME, 'true');

    tracker.track('Closed header referral tooltip cta', {
      category: 'Referral header tooltip',
    });
  }

  @autobind
  @action('onOpenBasketPanel - Header')
  onOpenBasketPanel(event) {
    const {tracker} = this.props;

    this.showBasketPanel = true;

    tracker.track('Clicked to open basket panel', {
      category: 'Header',
    });
  }

  @computed get headerLinks() {
    const {navigation: {header}} = this.props;
    const nav = header.navigation.map(item => (
      <Link
        activeClassName="site-header-menu-item--active"
        className="site-header-item"
        data-qa={`site-header-link-${item.title.replace(/\s/g, '-').toLowerCase()}`}
        to={item.url}
        key={`site-header-link-${item.title.replace(/\s/g, '-').toLowerCase()}`}
      >
        {item.title}
      </Link>
    ));
    return nav;
  }

  @computed get headerLinksLogin() {
    const { navigation: {header}} = this.props;
    const navLogin = header.navigationLogin.map(item => (
      <Link
        className="site-header-item"
        data-qa={`site-header-link-${item.title.replace(/\s/g, '-').toLowerCase()}`}
        to={item.url}
        key={`site-header-login-link-${item.title.replace(/\s/g, '-').toLowerCase()}`}
      >
        {item.title}
      </Link>
    ));
    return navLogin;
  }

  render() {
    const {showReferralPopup, userLoggedIn} = this;
    const {
      selection,
      useHeading = false,
    } = this.props;
    const { i18n } = this.context;

    return (
      <div className="fixed-header-placeholder">
        <div className="fixed-header">
          <div className="site-header">
            <Link to={`/${i18n.lc}/`} className="site-header-col site-header-col--logo">
              <Logo useHeading={useHeading} dark />
            </Link>
            <div className="site-header-col site-header-col--country-dropdown">
              <CountrySelect zIndex={11} />
            </div>
            <div className="site-header--show-desktop site-header-col site-header-col--expanded">
              {this.headerLinks}
            </div>
            <SessionSplit
              loggedIn={user => (
                <AccountDetails user={user} />
              )}
              loggedOut={() => (
                <div className="site-header--show-desktop site-header-col site-header-col--side-links">
                  {this.headerLinksLogin}
                </div>
              )}
            />
            <HeaderBasketSectionWrapper
              basketItems={selection.basketItems}
              onOpenBasketPanel={this.onOpenBasketPanel}
              userLoggedIn={userLoggedIn}
            />
            {showReferralPopup && (
              <Tooltip
                containerClassName="site-header__referral-tooltip"
                align="right"
                qaName="header-referral-tooltip"
                key="site-header-referral-tooltip"
                width={435}
                zIndex={20}
                topOffset={10}
                onClose={this.onCloseTooltip}
                hide={!this.showReferralPopup}
              >
                <h2 className="site-header__referral-tooltip-title">
                  {i18n._x('Know a friend who\'d love Urban?', 'Header referral tooltip')}
                </h2>
                <p className="site-header__referral-tooltip-paragraph">
                  {i18n.__react('Give them %(referralValue)s off with your unique referral code and earn your way to a free treatment.', {
                    referralValue: this.friendReferralDiscount,
                  }, 'Booking details page referral box')}
                </p>
                <Button
                  type={ButtonType.button}
                  notRounded
                  block
                  onClick={this.clickedReferNow}
                >
                  {i18n._x('Refer now', 'Homepage referral tooltip')}
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="site-header--show-mobile site-header-col site-header__mobile-menu-container">
          <input
            type="checkbox"
            id="page-menu-checkbox"
            value="toggle"
            ref={(menuCheckbox) => { this.menuCheckbox = menuCheckbox; }}
            checked={this.state.menuIsOpen}
            onChange={this.onChangeMenuCheckbox}
          />
          {this.state.menuIsOpen ? <DisableBodyScroll /> : null}
          <label
            htmlFor="page-menu-checkbox"
            className="site-header-item page-menu-button"
          >
            <span className="open">
              <ProgressiveImage
                alt=""
                disableWebp
                lowResImg={hamburger}
                height="11"
                highResImg={hamburger}
                highResImg2x={hamburger}
                width="18"
              />
            </span>
            <span className="close close-button" />
          </label>
          <label
            htmlFor="page-menu-checkbox"
            className="page-menu-overlay"
          />
          <PageMenu />
        </div>
        <ContextualMenu />
        {this.showBasketPanel ? (
          <RenderToContextual
            onClose={this.onCloseBasketPanel}
          >
            <aside className="treatment-selection-panel v-container">
              <Basket
                booking={{
                  address: this.props.address,
                  basket: this.props.basket,
                  selection: this.props.selection,
                }}
                onClose={this.onCloseBasketPanel}
              />
            </aside>
          </RenderToContextual>
        ) : null}
      </div>
    );
  }
}

export default Header;



// WEBPACK FOOTER //
// ./spa/Components/Header.jsx